import { memo, useCallback, type FC } from 'react';

import {
  ThreadListItemByIndexProvider,
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAuiState,
  useAui,
} from '@assistant-ui/react';
import { ArchiveIcon, CopyIcon, EllipsisIcon, MessageSquareOffIcon, PlusIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { ThreadListItemStatusIndicator } from '@/components/assistant-ui/thread-list-item-status';
import { computeRecencyOrder } from '@/components/assistant-ui/thread-list-order';
import { useConfigValue } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { useToasterId } from '@/components/toaster-context';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCloseSidebarIfOverlay } from '@/components/ui/sidebar';
import { useRequestStorageAccess } from '@/lib/storage/use-storage-access';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useCrossTabSync } from '@/lib/sync/hooks/use-cross-tab-sync';
import { useThreadStatusStore } from '@/lib/thread-status/store';
import { emitThreadSwitchStart } from '@/lib/thread-switch-signal';

function useThreadClick() {
  const closeSidebarIfOverlay = useCloseSidebarIfOverlay();
  const { requestOnInteraction } = useRequestStorageAccess();

  return () => {
    document.querySelector('[data-pending-active]')?.removeAttribute('data-pending-active');
    document.querySelector('[data-pending-switch]')?.removeAttribute('data-pending-switch');
    requestOnInteraction();
    closeSidebarIfOverlay();
  };
}

export const ThreadList: FC = () => {
  return (
    <ThreadListPrimitive.Root className="aui-root * aui-thread-list-root flex flex-col items-stretch gap-1.5 border-border outline-ring/50">
      <ThreadListItems />
    </ThreadListPrimitive.Root>
  );
};

export const ThreadListNew: FC = () => {
  const t = useTranslations();
  const handleClick = useThreadClick();

  return (
    <ThreadListPrimitive.New asChild>
      <Button
        onClick={() => handleClick()}
        className="aui-thread-list-new flex w-full items-center justify-start gap-1 rounded-lg px-2.5 py-2 text-start hover:bg-accent hover:text-accent-foreground data-active:bg-accent data-active:text-accent-foreground"
        variant="ghost"
      >
        <PlusIcon />
        {t('threadList.newThreadButton')}
      </Button>
    </ThreadListPrimitive.New>
  );
};

const renderThreadListItem = () => <ThreadListItem />;

const ThreadListItems: FC = () => {
  // The recency re-sort is part of the per-tenant server-persistence bundle; non-opted
  // (production) tenants keep the framework's vanilla ordering byte-identically.
  const serverPersistence = useConfigValue<'local' | 'server'>('persistence') === 'server';
  if (!serverPersistence) {
    return <ThreadListPrimitive.Items>{renderThreadListItem}</ThreadListPrimitive.Items>;
  }
  return <RecencyOrderedThreadListItems />;
};

const RecencyOrderedThreadListItems: FC = () => {
  // Recency-DESC ordering: the adapter returns updatedAt DESC at boot, but aui APPENDS newly
  // initialized/adopted threads to the END of threadIds and never reorders on activity — so a
  // just-created thread landed at the bottom. Render by max(lastMessageAt, lastActivityAt)
  // (threads without either — brand-new/in-flight — sort first). `lastMessageAt` comes from boot
  // metadata and is NOT bumped when a new message streams, so continuing an OLD thread wouldn't
  // re-float without the live `activityAt` signal (run start/end, keyed by durable uuid).
  // Indices MUST be threadIds-relative: the by-index provider resolves
  // `threads.item({index, archived: false})` against threadIds, while threadItems also holds the
  // not-yet-listed boot 'new' thread (index past threadIds ends → "Key undefined" render crash).
  const activityAt = useThreadStatusStore(state => state.activityAt);
  const orderedIndices = useAuiState(state =>
    computeRecencyOrder({
      threadIds: state.threads.threadIds,
      threadItems: state.threads.threadItems,
      activityAt,
    }).join(','),
  );
  // Durable ids for stable React keys: keying by position would let a reload/reorder reuse a row
  // instance for a different thread (stale menu/focus). The not-yet-listed boot 'new' thread has no
  // id (index past the array), so it falls back to an index-based key — the one case that can't collide.
  const threadIdsKey = useAuiState(state => state.threads.threadIds.join(','));
  const threadIds = threadIdsKey ? threadIdsKey.split(',') : [];

  return (
    <>
      {orderedIndices
        .split(',')
        .filter(Boolean)
        .map(indexString => {
          const index = Number(indexString);
          return (
            <ThreadListItemByIndexProvider
              key={threadIds[index] ?? `boot-${index}`}
              index={index}
              archived={false}
            >
              <ThreadListItem />
            </ThreadListItemByIndexProvider>
          );
        })}
    </>
  );
};

export const ThreadListEmpty: FC = () => {
  const t = useTranslations('readOnly');
  const threadCount = useAuiState(state => state.threads.threadItems.length);

  if (threadCount > 0) return null;

  return (
    <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
      <MessageSquareOffIcon className="size-6 text-muted-foreground/50" />
      <p className="text-xs text-muted-foreground/70">{t('noThreads')}</p>
    </div>
  );
};

const ThreadListItem: FC = memo(function ThreadListItem() {
  const handleClick = useThreadClick();
  const isReadOnly = useReadOnly();
  const aui = useAui();

  const handleItemClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const isCurrentThread = !!e.currentTarget.closest('[data-active]');

      if (isCurrentThread) {
        handleClick();
        return;
      }

      // Prevent the primitive's built-in switch — we'll trigger it manually
      // after the overlay fade-in completes.
      e.preventDefault();

      handleClick();

      // Optimistic: visually mark clicked thread as active and suppress the
      // library's data-active on the old thread until the real switch fires.
      const listRoot = e.currentTarget.closest('.aui-thread-list-root');
      const itemRoot = e.currentTarget.closest('.aui-thread-list-item');
      itemRoot?.setAttribute('data-pending-active', 'true');
      listRoot?.setAttribute('data-pending-switch', 'true');

      emitThreadSwitchStart(() => {
        aui.threadListItem.switchTo();
      });
    },
    [aui, handleClick],
  );

  return (
    <ThreadListItemPrimitive.Root className="group/thread-item aui-thread-list-item relative flex items-center overflow-hidden rounded-lg focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none data-active:bg-accent data-active:text-accent-foreground">
      <ThreadListItemPrimitive.Trigger
        className="aui-thread-list-item-trigger min-w-0 grow cursor-pointer px-3 py-2 text-start"
        onClick={handleItemClick}
      >
        <ThreadListItemTitle />
      </ThreadListItemPrimitive.Trigger>
      <ThreadListItemStatusIndicator />
      {!isReadOnly && <ThreadListItemMenu />}
    </ThreadListItemPrimitive.Root>
  );
});

const ThreadListItemTitle: FC = memo(function ThreadListItemTitle() {
  const t = useTranslations();
  // Native title attr = the full title on hover, instead of a real tooltip.
  const title = useAuiState(({ threadListItem }) => threadListItem?.title);

  return (
    <span className="aui-thread-list-item-title block truncate text-sm" title={title || undefined}>
      <ThreadListItemPrimitive.Title fallback={t('threadList.itemTitleFallback')} />
    </span>
  );
});

const ThreadListItemMenu: FC = memo(function ThreadListItemMenu() {
  const t = useTranslations();
  const { emit } = useCrossTabSync();
  const toasterId = useToasterId();
  const threadItem = useAuiState(state => state.threadListItem);
  const refId = threadItem?.remoteId ?? threadItem?.id;
  const remoteId = threadItem?.remoteId;

  const handleCopyRefId = useCallback(async () => {
    if (!refId) return;
    await navigator.clipboard.writeText(refId);
    toast.success(t('threadList.refIdCopied'), { toasterId });
  }, [refId, t, toasterId]);

  const handleArchive = useCallback(() => {
    if (remoteId) {
      emit(SYNC_EVENTS.THREAD_ARCHIVE, remoteId);
    }
  }, [emit, remoteId]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="aui-thread-list-item-menu absolute inset-e-1 top-1/2 z-10 size-7 -translate-y-1/2 cursor-pointer rounded-md p-0 text-transparent group-data-active/thread-item:text-accent-foreground pointer-fine:group-focus-within/thread-item:text-accent-foreground pointer-fine:group-hover/thread-item:text-accent-foreground"
          aria-label={t('threadList.menuTooltip')}
        >
          <EllipsisIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4} onCloseAutoFocus={e => e.preventDefault()}>
        <DropdownMenuItem onClick={handleCopyRefId}>
          <CopyIcon />
          {t('threadList.copyRefId')}
        </DropdownMenuItem>
        <ThreadListItemPrimitive.Archive asChild onClick={handleArchive}>
          <DropdownMenuItem>
            <ArchiveIcon />
            {t('threadList.archiveTooltip')}
          </DropdownMenuItem>
        </ThreadListItemPrimitive.Archive>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
